import { Node, mergeAttributes } from '@tiptap/core'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    Video: {
      setVideo: (options: {
        src: string
        caption?: string
        width?: number
        height?: number
        aspectRatio?: string
      }) => ReturnType
      /**
       * Set the video's caption.
       */
      setVideoCaption: (caption: string | null) => ReturnType
    }
  }
}

export const Video = Node.create({
  name: 'video', // unique name for the Node
  group: 'block', // belongs to the 'block' group of extensions
  selectable: true, // so we can select the video
  draggable: true, // so we can drag the video
  atom: true, // is a single unit

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute('src'),
        renderHTML: (attributes) => {
          if (!attributes.src) {
            return {}
          }

          return {
            src: attributes.src,
          }
        },
      },
      caption: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-caption'),
        renderHTML: (attributes) => {
          if (!attributes.caption) {
            return {}
          }

          return {
            'data-caption': attributes.caption,
          }
        },
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute('width'),
        renderHTML: (attributes) => {
          if (!attributes.width) {
            return {}
          }

          return {
            width: attributes.width,
          }
        },
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute('height'),
        renderHTML: (attributes) => {
          if (!attributes.width) {
            return {}
          }

          return {
            height: attributes.height,
          }
        },
      },
      aspectRatio: {
        default: null,
        parseHTML: (element) => element.getAttribute('aspect-ratio'),
        renderHTML: (attributes) => {
          if (!attributes.aspectRatio) {
            return {}
          }

          return {
            'aspect-ratio': attributes.aspectRatio,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'video',
        getAttrs: (dom) => {
          if (!(dom instanceof HTMLVideoElement)) return {}
          return {
            src: dom.getAttribute('src'),
            width: dom.getAttribute('width'),
            height: dom.getAttribute('height'),
            aspectRatio: dom.getAttribute('aspect-ratio'),
            ['data-caption']: dom.getAttribute('data-caption'),
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['video', mergeAttributes(HTMLAttributes)]
  },

  addCommands() {
    return {
      setVideo:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          })
        },
      setVideoCaption:
        (caption) =>
        ({ commands }) => {
          return commands.updateAttributes('video', { caption })
        },
    }
  },
})
